import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { ArrowLeft, Users, Loader2, ArrowUpRight } from "lucide-react";
import { Footer } from "@/components/landing/FooterCTA";
import { SponsorBanner } from "@/components/Sponsors";
import { playerAvatar } from "@/components/plantel/PlayerCard";
import { Navbar } from "@/components/landing/Navbar";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import logo from "../assets/logo.png";

function RosterCard({ roster, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.3) }}
    >
      <Link
        to={`/plantel/${roster.slug}`}
        data-testid={`roster-card-${roster.slug}`}
        className="group block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
      >
        <div className="relative aspect-[4/3] bg-navy overflow-hidden">
          <img
            src={roster.cover_url || roster.sample_photo_url || playerAvatar(roster.title)}
            alt={roster.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider text-navy bg-gold px-2.5 py-1 rounded-full">
            {roster.category}
          </span>
          <span className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
          <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
            <div>
              <div className="text-xs font-semibold text-gold">Temporada {roster.season}</div>
              <h3 className="text-lg font-bold text-white leading-tight" style={{ fontFamily: "Outfit" }}>
                {roster.category}{roster.subcategory ? ` · ${roster.subcategory}` : ""}
              </h3>
            </div>
            <ArrowUpRight className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
        <div className="px-4 sm:px-5 py-3.5 flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy">
            <Users className="h-4 w-4 text-gold" /> {roster.player_count} jugador{roster.player_count === 1 ? "" : "es"}
          </span>
          {roster.description && <span className="text-xs text-gray-400 truncate max-w-[55%]">{roster.description}</span>}
        </div>
      </Link>
    </motion.div>
  );
}

export default function PlantelPage() {
  const [rosters, setRosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("Todas");

  useEffect(() => {
    api.get("/plantel/rosters").then(({ data }) => setRosters(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => ["Todas", ...Array.from(new Set(rosters.map((r) => r.category)))],
    [rosters]
  );
  const filtered = category === "Todas" ? rosters : rosters.filter((r) => r.category === category);

  return (
    <div className="min-h-screen bg-[#eef1f7] flex flex-col">
      <Navbar />
      <div className="pt-16 sm:pt-20">
        <Breadcrumbs items={[{ label: "Plantel" }]} />
      </div>

      <section className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 pt-4">
          <span className="section-tag section-tag--dark">Plantel</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-white">Nuestros planteles</h1>
          <p className="mt-3 text-base sm:text-lg text-white/75 max-w-2xl">
            Cada temporada, cada categoría. Elegí un plantel para ver a todos sus jugadores y entrar a sus fichas.
          </p>
        </div>
      </section>

      <div className="bg-white/70 backdrop-blur border-b border-gray-100 sticky top-16 sm:top-20 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`plantel-filter-${c}`}
              onClick={() => setCategory(c)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                category === c ? "bg-navy text-white" : "bg-white text-navy border border-gray-200 hover:border-navy/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-8">
        {loading ? (
          <div className="py-20 flex justify-center text-navy/40"><Loader2 className="h-7 w-7 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-gray-400">
            <Users className="h-10 w-10 mx-auto" />
            <p className="mt-3">Todavía no hay planteles cargados.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((r, i) => <RosterCard key={r.id} roster={r} i={i} />)}
          </div>
        )}
      </main>

      <SponsorBanner placement="banner_plantel" />
      <Footer />
    </div>
  );
}
